import React from "react";
import AnimatedNumber from "@/components/AnimatedNumber";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { PanelShell, type PanelMeta } from "./PanelShell";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { Switch } from "@/components/ui/switch";

type Snapshot = {
  Turnover_Year1?: number | null;
  Turnover_Year2?: number | null;
  Turnover_Year3?: number | null;
  Profit_Before_Tax_Year1?: number | null;
  Profit_Before_Tax_Year2?: number | null;
  Profit_Before_Tax_Year3?: number | null;
  Total_Assets_Year1?: number | null;
  Total_Assets_Year2?: number | null;
  Total_Assets_Year3?: number | null;
  Employees_Year1?: number | null;
  Turnover_Band?: string | null;
};

type FinancialRow = {
  label: string;
  y1?: number | null;
  y2?: number | null;
  y3?: number | null;
  format: (value?: number | null) => string;
};

type HowPanelProps = {
  panel: PanelMeta;
  headerColor: string;
  layoutEdit: boolean;
  reportMode?: boolean;
  reportView?: "table" | "chart";
  reportHideSummary?: boolean;
  snapshot: Snapshot | null;
  yearLabels: { year1: string; year2: string; year3: string };
  isMounted: boolean;
  financialRows: FinancialRow[];
};

export function HowPanel({
  panel,
  headerColor,
  layoutEdit,
  reportMode = false,
  reportView,
  reportHideSummary = false,
  snapshot,
  yearLabels,
  isMounted,
  financialRows,
}: HowPanelProps) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [containerSize, setContainerSize] = React.useState({ width: 0, height: 0 });

  React.useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setContainerSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
  const formatCompactCurrency = (value: number) => {
    const absValue = Math.abs(value);
    const maximumFractionDigits = absValue >= 1_000_000 ? 1 : absValue >= 1_000 ? 1 : 0;
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      notation: "compact",
      compactDisplay: "short",
      maximumFractionDigits,
    }).format(value);
  };
  const formatCompactNumber = (value: number) => {
    const absValue = Math.abs(value);
    const maximumFractionDigits = absValue >= 1_000 ? 1 : 0;
    return new Intl.NumberFormat("en-GB", {
      notation: "compact",
      compactDisplay: "short",
      maximumFractionDigits,
    }).format(value);
  };

  const chartData = [
    { year: yearLabels.year3, turnover: snapshot?.Turnover_Year3 ?? null },
    { year: yearLabels.year2, turnover: snapshot?.Turnover_Year2 ?? null },
    { year: yearLabels.year1, turnover: snapshot?.Turnover_Year1 ?? null },
  ].filter((row) => isNumber(row.turnover));

  const hasChartData = chartData.length > 0;

  const [viewMode, setViewMode] = React.useState<"chart" | "table">(() =>
    hasChartData ? "chart" : "table"
  );

  const showTable = reportMode ? true : viewMode === "table";
  const maxChartHeight = 320;
  const reservedHeight = 140;
  const availableHeight = Math.max(0, containerSize.height - reservedHeight);
  const chartHeight = reportMode ? 360 : Math.min(maxChartHeight, availableHeight);

  const chartConfig = {
    turnover: { label: "Turnover", color: headerColor },
  } satisfies ChartConfig;

  return (
    <PanelShell panel={panel} headerColor={headerColor} layoutEdit={layoutEdit} reportMode={reportMode}>
      <div
        ref={containerRef}
        className={`${reportMode ? "mt-2" : "mt-3"} flex flex-col gap-3 text-[11px] text-muted-foreground min-h-0 h-full px-2 box-border`}
      >
        {!reportHideSummary && (
          <div
            className={`grid grid-cols-2 ${
              reportMode ? "gap-2 text-[10px]" : "gap-3 text-[11px]"
            } text-muted-foreground`}
          >
            <div>
              <span className="uppercase text-[10px] tracking-wide text-muted-foreground">
                Turnover ({yearLabels.year1})
              </span>
              <div className={`${reportMode ? "text-xs" : "text-sm"} font-semibold text-foreground`}>
                {isNumber(snapshot?.Turnover_Year1) ? (
                  <>
                    <AnimatedNumber
                      value={snapshot?.Turnover_Year1 ?? 0}
                      isActive={isMounted}
                      delay={300}
                      duration={1.4}
                      formatter={formatCompactCurrency}
                    />
                  </>
                ) : (
                  "—"
                )}
              </div>
            </div>
            <div>
              <span className="uppercase text-[10px] tracking-wide text-muted-foreground">Profit before tax</span>
              <div className={`${reportMode ? "text-xs" : "text-sm"} font-semibold text-foreground`}>
                {isNumber(snapshot?.Profit_Before_Tax_Year1) ? (
                  <>
                    <AnimatedNumber
                      value={snapshot?.Profit_Before_Tax_Year1 ?? 0}
                      isActive={isMounted}
                      delay={350}
                      duration={1.4}
                      formatter={formatCompactCurrency}
                    />
                  </>
                ) : (
                  "—"
                )}
              </div>
            </div>
            <div>
              <span className="uppercase text-[10px] tracking-wide text-muted-foreground">Total assets</span>
              <div className={`${reportMode ? "text-xs" : "text-sm"} font-semibold text-foreground`}>
                {isNumber(snapshot?.Total_Assets_Year1) ? (
                  <>
                    <AnimatedNumber
                      value={snapshot?.Total_Assets_Year1 ?? 0}
                      isActive={isMounted}
                      delay={400}
                      duration={1.4}
                      formatter={formatCompactCurrency}
                    />
                  </>
                ) : (
                  "—"
                )}
              </div>
            </div>
            <div>
              <span className="uppercase text-[10px] tracking-wide text-muted-foreground">Employees</span>
              <div className={`${reportMode ? "text-xs" : "text-sm"} font-semibold text-foreground`}>
                {isNumber(snapshot?.Employees_Year1) ? (
                  <AnimatedNumber
                    value={snapshot?.Employees_Year1 ?? 0}
                    isActive={isMounted}
                    delay={450}
                    duration={1.2}
                    formatter={formatCompactNumber}
                  />
                ) : (
                  "—"
                )}
              </div>
            </div>
            <div className="col-span-2 text-[10px] text-muted-foreground">
              {snapshot?.Turnover_Band ? `Turnover band: ${snapshot.Turnover_Band}` : "Latest accounts snapshot"}
            </div>
          </div>
        )}

        {!reportMode && hasChartData && (
          <div className="flex items-center justify-end gap-2 text-[10px] text-muted-foreground">
            <span className={viewMode === "chart" ? "text-foreground" : ""}>Chart</span>
            <Switch
              checked={viewMode === "table"}
              onCheckedChange={(checked) => setViewMode(checked ? "table" : "chart")}
            />
            <span className={viewMode === "table" ? "text-foreground" : ""}>Table</span>
          </div>
        )}

        {reportMode ? (
          <>
            {reportView !== "chart" ? (
              <div className="rounded-md border border-transparent bg-transparent box-border report-avoid-break">
                <table className="w-full text-[11px] report-table">
                  <thead className="bg-transparent">
                    <tr className="text-left text-[10px] uppercase tracking-wide" style={{ color: headerColor }}>
                      <th className="px-2 py-1">Metric</th>
                      <th className="px-2 py-1">{yearLabels.year1}</th>
                      <th className="px-2 py-1">{yearLabels.year2}</th>
                      <th className="px-2 py-1">{yearLabels.year3}</th>
                    </tr>
                  </thead>
                  <tbody className="text-foreground">
                    {financialRows.map((row) => (
                      <tr key={row.label} className="border-t border-border/60">
                        <td className="px-2 py-1 text-muted-foreground">{row.label}</td>
                        <td className="px-2 py-1">{row.format(row.y1)}</td>
                        <td className="px-2 py-1">{row.format(row.y2)}</td>
                        <td className="px-2 py-1">{row.format(row.y3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {reportView !== "table" ? (
              hasChartData ? (
                <div className="rounded-md border border-transparent bg-transparent px-2 py-2 overflow-hidden box-border report-avoid-break mt-2">
                  <div style={{ height: chartHeight }} className="w-full">
                    <ChartContainer config={chartConfig} className="h-full w-full">
                      <LineChart data={chartData} margin={{ top: 12, right: 16, bottom: 16, left: 12 }}>
                        <CartesianGrid vertical={false} />
                        <XAxis dataKey="year" tickLine={false} axisLine={false} tickMargin={6} />
                        <YAxis
                          tickLine={false}
                          axisLine={false}
                          tickMargin={6}
                          width={48}
                          tickFormatter={(value) => formatCompactCurrency(Number(value))}
                        />
                        <ChartTooltip
                          cursor={false}
                          content={<ChartTooltipContent formatter={(value) => formatCompactCurrency(Number(value))} />}
                        />
                        <Line
                          type="monotone"
                          dataKey="turnover"
                          stroke={headerColor}
                          strokeWidth={3}
                          dot={{ r: 2, fill: headerColor }}
                          activeDot={{ r: 4 }}
                          connectNulls
                          isAnimationActive={false}
                        />
                      </LineChart>
                    </ChartContainer>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">No multi-year financial data available.</div>
              )
            ) : null}
          </>
        ) : (
          <>
            {viewMode === "chart" && (
              <div className="rounded-md border border-border bg-card px-2 py-2 overflow-hidden box-border">
                {hasChartData ? (
                  <div style={{ height: chartHeight }} className="w-full">
                    <ChartContainer config={chartConfig} className="h-full w-full">
                      <LineChart data={chartData} margin={{ top: 12, right: 16, bottom: 16, left: 12 }}>
                        <CartesianGrid vertical={false} />
                        <XAxis dataKey="year" tickLine={false} axisLine={false} tickMargin={6} />
                        <YAxis
                          tickLine={false}
                          axisLine={false}
                          tickMargin={6}
                          width={48}
                          tickFormatter={(value) => formatCompactCurrency(Number(value))}
                        />
                        <ChartTooltip
                          cursor={false}
                          content={<ChartTooltipContent formatter={(value) => formatCompactCurrency(Number(value))} />}
                        />
                        <Line
                          type="monotone"
                          dataKey="turnover"
                          stroke={headerColor}
                          strokeWidth={3}
                          dot={{ r: 2, fill: headerColor }}
                          activeDot={{ r: 4 }}
                          connectNulls
                          isAnimationActive={false}
                        />
                      </LineChart>
                    </ChartContainer>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">No multi-year financial data available.</div>
                )}
              </div>
            )}

            {showTable && (
              <div
                className="rounded-md border border-border bg-card box-border overflow-auto flex-1 min-h-0"
                style={hasChartData ? { maxHeight: chartHeight, scrollbarGutter: "stable both-edges" } : { scrollbarGutter: "stable both-edges" }}
              >
                <table className="w-full text-[11px]">
                  <thead className="sticky top-0 bg-card">
                    <tr className="text-left text-[10px] uppercase tracking-wide" style={{ color: headerColor }}>
                      <th className="px-2 py-1">Metric</th>
                      <th className="px-2 py-1">{yearLabels.year1}</th>
                      <th className="px-2 py-1">{yearLabels.year2}</th>
                      <th className="px-2 py-1">{yearLabels.year3}</th>
                    </tr>
                  </thead>
                  <tbody className="text-foreground">
                    {financialRows.map((row) => (
                      <tr key={row.label} className="border-t border-border/60">
                        <td className="px-2 py-1 text-muted-foreground">{row.label}</td>
                        <td className="px-2 py-1">{row.format(row.y1)}</td>
                        <td className="px-2 py-1">{row.format(row.y2)}</td>
                        <td className="px-2 py-1">{row.format(row.y3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </PanelShell>
  );
}
